import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { Icon } from '@iconify/react';

interface DroneStatusProps {
  onClose: () => void;
}

interface DroneInfo {
  droneId: string;
  name: string;
  siteCode: string | null;
  willowId: string;
  status: 'online' | 'offline';
  lastSeenAt: string | null;
  armed: boolean | null;
  flightMode: string | null;
  batteryPercent: number | null;
  latitude: number | null;
  longitude: number | null;
  absoluteAltitudeM: number | null;
  relativeAltitudeM: number | null;
  rollDeg: number | null;
  pitchDeg: number | null;
  yawDeg: number | null;
  timestamp: string | null;
}

interface DroneApiResponse {
  success: boolean;
  willowId: string;
  count: number;
  drones: DroneInfo[];
}

const API_BASE_URL = 'https://qa-backend.willowyardportal.com/api/drone';

export default function DroneStatus({ onClose }: DroneStatusProps) {

  const { user, token } = useSelector((state: RootState) => ({
    user: state.user,
    token: state.token
  }));

  const [drones, setDrones] = useState<DroneInfo[]>([]);
  const [altitudeUnit, setAltitudeUnit] = useState<'ft' | 'm'>('ft');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestInProgress = useRef(false);

  const loadDrones = async () => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    try {
      if (!user?.willowId || !token) {
        setDrones([]);
        setError('Customer authentication is unavailable');
        setLoading(false);
        return;
      }

      const url = `${API_BASE_URL}/account/${encodeURIComponent(user.willowId)}/status`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: DroneApiResponse = await response.json();

      if (!result.success) {
        throw new Error('Drone API returned an error');
      }

      setDrones(result.drones);
      setError(null);

    } catch (err) {
      console.error('Failed to load drone status:', err);
      setError('Unable to load drone status');

    } finally {
      requestInProgress.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {

    loadDrones();

    // Refresh Drone Watch every 0.5 seconds.
    const interval = window.setInterval(() => {
      loadDrones();
    }, 500);

    return () => {
      window.clearInterval(interval);
    };

  }, [user?.willowId, token]);

  const getDroneStatusStyle = (status: string) => {

    if (status === 'online') {
      return 'border-green bg-[#E2FFF0] text-green';
    }

    return 'border-gray-400 bg-gray-100 text-gray-500';
  };

  const getBatteryStyle = (batteryPercent: number | null) => {

    if (batteryPercent === null) {
      return 'border-gray-400 bg-gray-100 text-gray-500';
    }

    if (batteryPercent >= 50) {
      return 'border-green bg-[#E2FFF0] text-green';
    }

    if (batteryPercent >= 20) {
      return 'border-[#CC9800] bg-[#FFFAEB] text-[#CC9800]';
    }

    return 'border-[#F60000] bg-[#FFE4E4] text-[#F60000]';
  };

  const formatStatus = (status: string) => {
    return status === 'online' ? 'Online' : 'Offline';
  };

  const formatBattery = (batteryPercent: number | null) => {

    if (batteryPercent === null) {
      return 'N/A';
    }

    return `${Math.round(batteryPercent)}%`;
  };

  const formatGps = (
    latitude: number | null,
    longitude: number | null
  ) => {

    if (latitude === null || longitude === null) {
      return 'N/A';
    }

    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  };

  const formatAltitude = (meters: number | null) => {
    if (meters == null || !Number.isFinite(meters)) return 'N/A';
    const value = altitudeUnit === 'ft' ? meters * 3.28084 : meters;
    return `${value.toFixed(1)} ${altitudeUnit}`;
  };

  const formatAngle = (degrees: number | null) => {
    if (degrees == null || !Number.isFinite(degrees)) return 'N/A';
    return `${degrees.toFixed(1)}°`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">

      <div className="w-full max-w-[1400px] max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">

          <div>
            <h2 className="text-2xl font-bold text-black-1 font-[Geist-Bold]">
              Drone Status
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Current Willow Yard drone fleet status
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black"
          >
            <Icon
              icon="gridicons:cross"
              width={28}
              height={28}
            />
          </button>

        </div>


        {/* Body */}
        <div className="p-6">

          {loading && (
            <div className="py-10 text-center text-gray-500">
              Loading drone status...
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-[#F60000] bg-[#FFE4E4] p-4 text-center text-[#F60000]">
              {error}
            </div>
          )}

          {!loading && !error && drones.length === 0 && (
            <div className="py-10 text-center text-gray-500">
              No drones assigned to this Willow account.
            </div>
          )}


          {!loading && !error && drones.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-end gap-3">
                <label htmlFor="drone-altitude-unit" className="text-sm font-medium text-gray-600">
                  Altitude Unit
                </label>
                <select
                  id="drone-altitude-unit"
                  value={altitudeUnit}
                  onChange={(event) => setAltitudeUnit(event.target.value as 'ft' | 'm')}
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-black-1"
                >
                  <option value="ft">Feet (ft)</option>
                  <option value="m">Meters (m)</option>
                </select>
              </div>

              {/* Desktop Table */}
              <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">

                <table className="w-full min-w-[1100px] text-sm">

                  <thead>
                    <tr className="bg-[#EDEDED] text-blue">

                      <th className="p-4 text-left font-[Geist-Medium]">
                        Drone ID
                      </th>

                      <th className="p-4 text-center font-[Geist-Medium]">
                        Drone Status
                      </th>

                      <th className="p-4 text-center font-[Geist-Medium]">
                        Battery
                      </th>

                      <th className="p-4 text-center font-[Geist-Medium]">
                        GPS Location
                      </th>

                      {['Altitude', 'Roll', 'Pitch', 'Yaw'].map((label) => (
                        <th key={label} className="p-3 text-center font-[Geist-Medium]">
                          {label}
                        </th>
                      ))}

                    </tr>
                  </thead>

                  <tbody>

                    {drones.map((drone) => (

                      <tr
                        key={drone.droneId}
                        className="border-t border-gray-200"
                      >

                        <td className="p-4 font-semibold text-black-1">
                          {drone.droneId}
                        </td>

                        <td className="p-4 text-center">
                          <span
                            className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${getDroneStatusStyle(drone.status)}`}
                          >
                            {formatStatus(drone.status)}
                          </span>
                        </td>

                        <td className="p-4 text-center">
                          <span
                            className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${getBatteryStyle(drone.batteryPercent)}`}
                          >
                            {formatBattery(drone.batteryPercent)}
                          </span>
                        </td>

                        <td className="p-4 text-center font-[Geist-Medium] text-black-1">
                          {formatGps(
                            drone.latitude,
                            drone.longitude
                          )}
                        </td>

                        <td className="p-3 text-center whitespace-nowrap text-black-1">
                          {formatAltitude(drone.relativeAltitudeM)}
                        </td>
                        <td className="p-3 text-center whitespace-nowrap text-black-1">
                          {formatAngle(drone.rollDeg)}
                        </td>
                        <td className="p-3 text-center whitespace-nowrap text-black-1">
                          {formatAngle(drone.pitchDeg)}
                        </td>
                        <td className="p-3 text-center whitespace-nowrap text-black-1">
                          {formatAngle(drone.yawDeg)}
                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>


              {/* Mobile Cards */}
              <div className="space-y-4 md:hidden">

                {drones.map((drone) => (

                  <div
                    key={drone.droneId}
                    className="rounded-lg border border-gray-200 p-4"
                  >

                    <div className="mb-4">
                      <p className="text-sm text-gray-500">
                        Drone ID
                      </p>

                      <p className="mt-1 text-lg font-semibold text-black-1">
                        {drone.droneId}
                      </p>
                    </div>


                    <div className="mb-4">
                      <p className="mb-2 text-sm text-gray-500">
                        Drone Status
                      </p>

                      <span
                        className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${getDroneStatusStyle(drone.status)}`}
                      >
                        {formatStatus(drone.status)}
                      </span>
                    </div>


                    <div className="mb-4">
                      <p className="mb-2 text-sm text-gray-500">
                        Battery
                      </p>

                      <span
                        className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${getBatteryStyle(drone.batteryPercent)}`}
                      >
                        {formatBattery(drone.batteryPercent)}
                      </span>
                    </div>


                    <div>
                      <p className="mb-2 text-sm text-gray-500">
                        GPS Location
                      </p>

                      <p className="font-semibold text-black-1">
                        {formatGps(
                          drone.latitude,
                          drone.longitude
                        )}
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {[
                        { label: 'Altitude', value: formatAltitude(drone.relativeAltitudeM) },
                        { label: 'Roll', value: formatAngle(drone.rollDeg) },
                        { label: 'Pitch', value: formatAngle(drone.pitchDeg) },
                        { label: 'Yaw', value: formatAngle(drone.yawDeg) }
                      ].map((item) => (
                        <div key={item.label}>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="mt-1 font-semibold text-black-1">{item.value}</p>
                        </div>
                      ))}
                    </div>

                  </div>

                ))}

              </div>
            </>
          )}


          {/* Fleet */}
          <div className="mt-6 rounded-lg bg-gray-50 p-4">

            <p className="text-sm text-gray-500">
              Assigned Fleet
            </p>

            <p className="mt-1 text-lg font-semibold text-black-1">
              {drones.length} Drone{drones.length === 1 ? '' : 's'}
            </p>

          </div>

        </div>


        {/* Footer */}
        <div className="flex justify-end border-t px-6 py-4">

          <button
            onClick={onClose}
            className="rounded bg-blue px-6 py-2 font-semibold text-white hover:opacity-90"
          >
            Close
          </button>

        </div>

      </div>

    </div>
  );
}
